// html2canvas 1.4 parses CSS colors itself and cannot read modern functions.
// Let the browser convert them into sRGB pixels, only in the export document.
const MODERN_COLOR = /\b(?:color|color-mix|lab|lch|oklab|oklch|light-dark)\(/i;
const PAINT_PROPERTIES = [
  "color",
  "background-color",
  "background-image",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "outline-color",
  "text-decoration-color",
  "box-shadow",
  "text-shadow",
  "list-style-image",
];

export function normalizeReportColors(root) {
  const doc = root.ownerDocument;
  const view = doc.defaultView;
  const colors = new Map();
  let context;

  const convertColor = (color) => {
    if (colors.has(color)) return colors.get(color);
    if (!context) {
      const canvas = doc.createElement("canvas");
      canvas.width = canvas.height = 1;
      context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) throw new Error("Unable to convert report colors");
    }
    context.clearRect(0, 0, 1, 1);
    context.fillStyle = color;
    context.fillRect(0, 0, 1, 1);
    const [r, g, b, alpha] = context.getImageData(0, 0, 1, 1).data;
    const result = `rgba(${r}, ${g}, ${b}, ${alpha / 255})`;
    colors.set(color, result);
    return result;
  };

  const convertValue = (value) => {
    let match;
    while ((match = MODERN_COLOR.exec(value))) {
      const start = match.index;
      let end = start + match[0].length;
      let depth = 1;
      while (end < value.length && depth > 0) {
        if (value[end] === "(") depth++;
        if (value[end] === ")") depth--;
        end++;
      }
      if (depth) throw new Error("Unbalanced report color function");
      value =
        value.slice(0, start) +
        convertColor(value.slice(start, end)) +
        value.slice(end);
    }
    // The effort-rate gradient uses two positions per stop. html2canvas needs
    // each position written as a separate stop to retain the hard zone edges.
    return /\b(?:linear|radial)-gradient\(/.test(value)
      ? value.replace(
          /(rgba?\([^)]*\))\s+(-?[\d.]+(?:%|px)?)\s+(-?[\d.]+(?:%|px)?)/g,
          "$1 $2, $1 $3"
        )
      : value;
  };

  const pseudoRules = [];
  [root, ...root.querySelectorAll("*")].forEach((element, index) => {
    [null, "::before", "::after"].forEach((pseudo) => {
      const computed = view.getComputedStyle(element, pseudo);
      if (
        pseudo &&
        (!computed.content || ["none", "normal"].includes(computed.content))
      )
        return;
      const overrides = PAINT_PROPERTIES.flatMap((property) => {
        const value = computed.getPropertyValue(property);
        return MODERN_COLOR.test(value)
          ? [[property, convertValue(value)]]
          : [];
      });
      if (pseudo && overrides.length) {
        element.setAttribute("data-report-color", String(index));
        pseudoRules.push(
          `[data-report-color="${index}"]${pseudo} {${overrides.map(([property, value]) => `${property}:${value} !important;`).join("")}}`
        );
      } else {
        overrides.forEach(([property, value]) =>
          element.style.setProperty(property, value, "important")
        );
      }
    });
  });
  if (pseudoRules.length) {
    const style = doc.createElement("style");
    style.textContent = pseudoRules.join("\n");
    doc.head.appendChild(style);
  }
}
